import type { OrderItem, OrderState, OrderStatus } from '@/types/cart';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Order store interface
 */
interface OrderStore extends OrderState {
  // Actions
  addItem: (item: Omit<OrderItem, 'lineTotal'>) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string) => void;
  clearOrder: () => void;
  setOrder: (order: { orderId: string; items: OrderItem[]; total: number; status?: OrderStatus }) => void;
  setStatus: (status: OrderStatus) => void;
  setLoading: (isLoading: boolean) => void;

  // Helper functions
  getItemCount: () => number;
  isOrderEmpty: () => boolean;
  getItemById: (itemId: string) => OrderItem | undefined;
  hasItem: (menuItemId: string) => boolean;

  // Legacy aliases
  clearCart: () => void;
  isCartEmpty: () => boolean;
}

/**
 * Order Store (Zustand + localStorage persistence)
 * Manages customer order state with automatic persistence
 */
export const useCartStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      // Initial state
      orderId: null,
      items: [],
      total: 0,
      status: 'draft',
      isLoading: false,

      // Add item to cart
      addItem: (item) => {
        // Enforce cart size limit (10 items max for MVP)
        const MAX_CART_ITEMS = 10;
        const currentItemCount = get().items.length;
        
        if (currentItemCount >= MAX_CART_ITEMS) {
          throw new Error(`Cart is full. Maximum ${MAX_CART_ITEMS} items allowed.`);
        }

        const lineTotal = item.quantity * item.unitPrice;
        const newItem = { ...item, lineTotal };

        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (i) => i.menuItemId === item.menuItemId
          );

          if (existingItemIndex >= 0) {
            // Update quantity if item already exists
            const updatedItems = [...state.items];
            const existingItem = updatedItems[existingItemIndex];
            updatedItems[existingItemIndex] = {
              ...existingItem,
              quantity: existingItem.quantity + item.quantity,
              lineTotal: (existingItem.quantity + item.quantity) * existingItem.unitPrice,
            };

            const newTotal = updatedItems.reduce((sum, i) => sum + i.lineTotal, 0);

            return {
              items: updatedItems,
              total: newTotal,
            };
          }

          // Add new item
          const updatedItems = [...state.items, newItem];
          const newTotal = updatedItems.reduce((sum, i) => sum + i.lineTotal, 0);

          return {
            items: updatedItems,
            total: newTotal,
          };
        });
      },

      // Update item quantity
      updateQuantity: (itemId, quantity) => {
        set((state) => {
          const updatedItems = state.items.map((item) => {
            if (item.id === itemId) {
              return {
                ...item,
                quantity,
                lineTotal: quantity * item.unitPrice,
              };
            }
            return item;
          });

          const newTotal = updatedItems.reduce((sum, item) => sum + item.lineTotal, 0);

          return {
            items: updatedItems,
            total: newTotal,
          };
        });
      },

      // Remove item from cart
      removeItem: (itemId) => {
        set((state) => {
          const updatedItems = state.items.filter((item) => item.id !== itemId);
          const newTotal = updatedItems.reduce((sum, item) => sum + item.lineTotal, 0);

          return {
            items: updatedItems,
            total: newTotal,
          };
        });
      },

      // Clear order
      clearOrder: () => {
        set({
          orderId: null,
          items: [],
          total: 0,
          status: 'draft',
        });
      },

      // Set order from backend response (replaces all order data)
      setOrder: (order) => {
        set({
          orderId: order.orderId,
          items: order.items,
          total: order.total,
          status: order.status || 'draft',
        });
      },

      // Set order status
      setStatus: (status) => {
        set({ status });
      },

      // Set loading state
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      // Helper: Get total item count
      getItemCount: () => {
        const { items } = get();
        return items.reduce((count, item) => count + item.quantity, 0);
      },

      // Helper: Check if order is empty
      isOrderEmpty: () => {
        const { items } = get();
        return items.length === 0;
      },

      // Legacy aliases
      clearCart: () => get().clearOrder(),
      isCartEmpty: () => get().isOrderEmpty(),

      // Helper: Get item by ID
      getItemById: (itemId) => {
        const { items } = get();
        return items.find((item) => item.id === itemId);
      },

      // Helper: Check if menu item is in cart
      hasItem: (menuItemId) => {
        const { items } = get();
        return items.some((item) => item.menuItemId === menuItemId);
      },
    }),
    {
      name: 'customer-order', // localStorage key
      partialize: (state) => ({
        orderId: state.orderId,
        items: state.items,
        total: state.total,
        status: state.status,
      }),
    }
  )
);
